import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../../firebase/config';
import { collection, query, getDocs, doc, updateDoc, orderBy } from 'firebase/firestore';

// IMPORTAMOS UTILIDADES DE NEGOCIO Y FIREBASE
import { deleteContractFull, rehabilitarContratoFull } from '../../../../firebase/firebaseUtils';
import { calculatePolicyStatus } from '../../../Formularios/formularioEmisionPolizas/policyUtils.js';

// COMPONENTES UI
import CardContrato from '../../../ui/CardContrato';
import ModalEdicionContrato from '../../../ui/ModalEdicionContrato'; 
import ModalAnulacionContratos from '../../../ui/ModalAnulacionContratos';
import ModalDetalleContrato from '../../../ui/ModalDetalleContrato'; 
import AuthModal from '../../../ui/AuthModal';

/**
 * ContratosTab - Gestión de Contratos ART con Sincronización Proactiva de Estados.
 */
const ContratosTab = ({ client }) => {
    const navigate = useNavigate();

    // --- ESTADOS DE DATOS ---
    const [contratos, setContratos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false); // Nuevo estado para updates silenciosos
    const [dbCompanias, setDbCompanias] = useState([]);

    // --- ESTADOS DE FILTRO ---
    const [showFilters, setShowFilters] = useState(true); 
    const [filters, setFilters] = useState({
        nContrato: '',
        estado: 'VIGENTE', 
        operacion: '',
        compania: ''
    });

    // --- ESTADOS DE MODALES ---
    const [selectedContrato, setSelectedContrato] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAnulacionModal, setShowAnulacionModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false); 
    const [authModalConfig, setAuthModalConfig] = useState({ open: false, type: '', item: null });

    // --- 1. LÓGICA DE SINCRONIZACIÓN ---
    const syncContratosStatus = async (contratosRaw) => {
        const syncPromises = contratosRaw.map(async (c) => {
            const currentStatus = (c.estadoPoliza || '').toUpperCase();
            const realStatus = calculatePolicyStatus(c.inicioVigencia, c.finVigencia, currentStatus);

            if (realStatus !== currentStatus) {
                try {
                    const ref = doc(db, `public/data/clientes/${client.id}/contratos`, c.id);
                    await updateDoc(ref, { estadoPoliza: realStatus });
                    return { ...c, estadoPoliza: realStatus };
                } catch (e) {
                    console.error(`Error sincronizando contrato ART ${c.id}:`, e);
                }
            }
            return c;
        });
        return await Promise.all(syncPromises);
    };

    const fetchContratos = useCallback(async () => {
        if (!client?.id) return;
        
        // Solo activamos el loading principal si no tenemos datos previos
        if (contratos.length === 0) setLoading(true);
        else setIsSyncing(true);

        try {
            const q = query(
                collection(db, `public/data/clientes/${client.id}/contratos`), 
                orderBy('fechaCarga', 'desc')
            );
            const snap = await getDocs(q);
            const rawData = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            const synchronizedData = await syncContratosStatus(rawData);
            setContratos(synchronizedData);
        } catch (error) {
            console.error("Error cargando contratos ART:", error);
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }
    }, [client?.id, contratos.length]);

    useEffect(() => {
        fetchContratos();
    }, [client?.id]); // Quitamos fetchContratos de dependencias para evitar loops

    // --- 2. CARGA DE COMPAÑÍAS ---
    useEffect(() => {
        const fetchMasters = async () => {
            try {
                const snap = await getDocs(collection(db, 'public/data/companias'));
                const cias = snap.docs.map(d => d.data().nombre).sort();
                setDbCompanias(cias);
            } catch (error) { console.error("Error loading companias", error); }
        };
        fetchMasters();
    }, []);

    // --- 3. LÓGICA DE FILTRADO ---
    const filteredContratos = useMemo(() => {
        return contratos.filter(c => {
            const matchContrato = filters.nContrato ? c.nContrato?.toLowerCase().includes(filters.nContrato.toLowerCase()) : true;
            const matchEstado = filters.estado ? (c.estadoPoliza || '').toUpperCase() === filters.estado.toUpperCase() : true;
            const matchOperacion = filters.operacion ? c.tipoOperacion === filters.operacion : true;
            const matchCompania = filters.compania ? c.compania === filters.compania : true;
            return matchContrato && matchEstado && matchOperacion && matchCompania;
        });
    }, [contratos, filters]);

    // --- HANDLERS ---
    const handleFilterChange = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

    /**
     * handleAuthActionConfirm - Procesa Borrado y Rehabilitación delegando en los servicios dedicados
     */
    const handleAuthActionConfirm = async () => {
        const { type, item } = authModalConfig;
        if (!item || !client?.id) return;

        try {
            if (type === 'DELETE') {
                await deleteContractFull(client.id, item.id);
                setContratos(prev => prev.filter(c => c.id !== item.id));
            } 
            else if (type === 'REHABILITAR') {
                // Invoca la utilidad centralizada que remueve los nulls de anulación físicamente
                await rehabilitarContratoFull(client.id, item.id);
                
                // Actualización optimista de UI local instantánea
                setContratos(prev => prev.map(c => 
                    c.id === item.id 
                        ? { 
                            ...c, 
                            estadoPoliza: 'Vigente',
                            motivoAnulacion: '',
                            fechaAnulacion: '',
                            quienGestionoAnulacion: '',
                            observacionesAnulacion: '' 
                          } 
                        : c
                ));

                // Disparador de background sync para re-sincronizar el estado final desde la DB
                fetchContratos();
            }
        } catch (error) {
            console.error(`Error en acción autorizada ${type}:`, error);
        } finally {
            setAuthModalConfig({ open: false, type: '', item: null });
        }
    };

    const handleEdit = (contrato) => { 
        setSelectedContrato(contrato); 
        setShowEditModal(true); 
    };

    // Si está cargando y no hay datos, mostramos el spinner a pantalla completa (dentro del tab)
    if (loading && contratos.length === 0) {
        return (
            <div className="p-12 text-center">
                <div className="inline-block w-8 h-8 border-4 border-t-indigo-600 border-gray-200 rounded-full animate-spin"></div>
                <p className="text-gray-500 mt-4 font-bold uppercase tracking-widest text-[10px]">Cargando contratos ART...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in text-left">
            {/* INDICADOR DE SINCRONIZACIÓN SILENCIOSA */}
            {isSyncing && (
                <div className="fixed top-20 right-10 z-50 bg-white shadow-lg border border-indigo-100 rounded-full px-4 py-2 flex items-center gap-2 animate-bounce">
                   <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                   <span className="text-[10px] font-bold text-indigo-600 uppercase">Actualizando datos...</span>
                </div>
            )}

            {/* PANEL DE FILTROS */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2 text-gray-700 font-bold text-lg">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                        <span>{filteredContratos.length} contratos</span>
                    </div>
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-indigo-600 font-semibold text-sm hover:text-indigo-800 transition-colors uppercase tracking-tighter">
                    {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </button>
            </div>

            {showFilters && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 transition-all duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">N° Contrato</label>
                            <input type="text" placeholder="Buscar..." value={filters.nContrato} onChange={e => handleFilterChange('nContrato', e.target.value)} className="input-filter" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Estado</label>
                            <select value={filters.estado} onChange={e => handleFilterChange('estado', e.target.value)} className="input-filter">
                                <option value="">Todos</option>
                                {optionsEstados.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Operación</label>
                            <select value={filters.operacion} onChange={e => handleFilterChange('operacion', e.target.value)} className="input-filter">
                                <option value="">Todas</option>
                                {[...new Set(contratos.map(c => c.tipoOperacion).filter(Boolean))].sort().map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Compañía</label>
                            <select value={filters.compania} onChange={e => handleFilterChange('compania', e.target.value)} className="input-filter">
                                <option value="">Todas</option>
                                {dbCompanias.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* LISTADO DE CARDS */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filteredContratos.length > 0 ? (
                    filteredContratos.map(contrato => (
                        <CardContrato 
                            key={contrato.id} 
                            contrato={contrato}
                            onView={(c) => { setSelectedContrato(c); setShowDetailModal(true); }}
                            onNavigate={(c) => navigate(`/clientes/${client.id}/contratos/${c.id}`)}
                            onEdit={handleEdit}
                            onDelete={() => setAuthModalConfig({ open: true, type: 'DELETE', item: contrato })}
                            onAnular={() => { setSelectedContrato(contrato); setShowAnulacionModal(true); }}
                            onRehabilitar={() => setAuthModalConfig({ open: true, type: 'REHABILITAR', item: contrato })}
                        />
                    ))
                ) : (
                    <div className="col-span-full py-16 text-center bg-white rounded-xl border border-dashed border-gray-200">
                        <p className="text-gray-500 font-medium">No se encontraron contratos con estos filtros.</p>
                    </div>
                )}
            </div>

            {/* MODALES */}
            {showEditModal && (
                <ModalEdicionContrato 
                    show={showEditModal} 
                    onClose={() => { setShowEditModal(false); setSelectedContrato(null); }} 
                    contrato={selectedContrato} 
                    clientData={client} 
                    onSuccess={fetchContratos} 
                />
            )}

            {showAnulacionModal && (
                <ModalAnulacionContratos 
                    show={showAnulacionModal} 
                    onClose={() => setShowAnulacionModal(false)} 
                    contrato={selectedContrato} 
                    clientId={client.id} 
                    onSuccess={fetchContratos} 
                />
            )}

            {showDetailModal && (
                <ModalDetalleContrato 
                    show={showDetailModal} 
                    onClose={() => { setShowDetailModal(false); setSelectedContrato(null); }} 
                    contrato={selectedContrato} 
                />
            )}

            <AuthModal 
                isOpen={authModalConfig.open} 
                onClose={() => setAuthModalConfig({ open: false, type: '', item: null })} 
                onConfirm={handleAuthActionConfirm} 
                title={authModalConfig.type === 'DELETE' ? 'Eliminar Contrato ART' : 'Rehabilitar Contrato ART'} 
                message={authModalConfig.type === 'DELETE' 
                    ? '¿Estás seguro? Esta acción borrará el contrato permanentemente.'
                    : '¿Deseas rehabilitar este contrato de ART? Su estado volverá a Vigente y se eliminarán físicamente los datos de anulación.'
                } 
                requireAuth={true} 
            />

            <style>{`
                .input-filter { width: 100%; border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; outline: none; background: white; }
                .input-filter:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

const optionsEstados = ['VIGENTE', 'PENDIENTE DE EMISIÓN', 'PENDIENTE DE INSPECCIÓN', 'PROPUESTA INGRESADA', 'VENCIDA', 'ANULADA', 'NO INICIADA'];

export default ContratosTab;